import React, { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import AdSlide from "./AdSlide";

const pubApi = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
const device = () => (window.innerWidth < 640 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop");

export default function AdSlider({ placement = "portal", slim = false, items: preset = null, settings: presetSettings = null, className = "", track = true }) {
  const [items, setItems] = useState(preset || []);
  const [settings, setSettings] = useState(presetSettings || { duration_sec: 6, transition: "slide", autoplay: true });
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const seen = useRef(new Set());
  const transition = settings.transition || "slide";
  const isFade = transition !== "slide";
  const [emblaRef, embla] = useEmblaCarousel({ loop: items.length > 1, align: "start", skipSnaps: false, duration: 24 });

  useEffect(() => {
    if (preset) { setItems(preset); return; }
    pubApi.get(`/ads/public/active?placement=${placement}`).then(r => { setItems(r.data.items || []); setSettings(r.data.settings || {}); }).catch(() => setItems([]));
  }, [placement, preset]);
  useEffect(() => { if (presetSettings) setSettings(presetSettings); }, [presetSettings]);

  const shown = useRef({});
  const trackEv = useCallback((ad, event, mediaId) => {
    if (!track || !ad) return;
    const media_id = mediaId || shown.current[ad.id] || undefined;
    pubApi.post("/ads/public/track", { ad_id: ad.id, event, placement, device: device(), media_id }).catch(() => {});
  }, [placement, track]);
  const onMediaShown = useCallback((ad, mid) => {
    shown.current[ad.id] = mid;
    const k = `${ad.id}:${mid}`; if (seen.current.has(k)) return;
    seen.current.add(k); trackEv(ad, "impression", mid);
  }, [trackEv]);

  useEffect(() => {
    const a = items[idx]; if (!a) return;
    const k = `${a.id}`; if (seen.current.has(k)) return;
    seen.current.add(k); trackEv(a, "impression");
  }, [idx, items, trackEv]);

  useEffect(() => {
    if (!embla) return;
    const onSel = () => setIdx(embla.selectedScrollSnap());
    embla.on("select", onSel); onSel();
    return () => embla.off("select", onSel);
  }, [embla]);

  const next = useCallback(() => { if (isFade) setIdx(i => (i + 1) % Math.max(items.length, 1)); else embla?.scrollNext(); }, [embla, isFade, items.length]);
  const prev = useCallback(() => { if (isFade) setIdx(i => (i - 1 + items.length) % Math.max(items.length, 1)); else embla?.scrollPrev(); }, [embla, isFade, items.length]);
  const goTo = useCallback((i) => { if (isFade) setIdx(i); else embla?.scrollTo(i); }, [embla, isFade]);

  useEffect(() => {
    if (!settings.autoplay || paused || items.length < 2) return;
    const cur = items[idx];
    if ((cur?.media || []).length) return; // media-driven slides advance via onMediaDone
    const ms = ((cur?.duration_sec) || settings.duration_sec || 6) * 1000;
    const t = setTimeout(next, ms); return () => clearTimeout(t);
  }, [idx, paused, items, settings, next]);
  const mediaDone = useCallback(() => { if (settings.autoplay && !paused && items.length > 1) next(); }, [settings.autoplay, paused, items.length, next]);

  // swipe for fade/zoom mode
  const touch = useRef(null);
  const onTouchStart = (e) => { touch.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => { if (touch.current == null) return; const dx = e.changedTouches[0].clientX - touch.current; touch.current = null; if (Math.abs(dx) > 40) (dx < 0 ? next() : prev()); };

  if (!items.length) return null;
  const h = slim ? "h-[64px] sm:h-[72px]" : "h-[200px] sm:h-[260px] md:h-[320px] lg:h-[360px]";
  const cur = items[idx];

  return (
    <section className={`relative select-none ${className}`} data-testid={`ad-slider-${placement}`} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} aria-roledescription="carousel">
      <div className={`relative ${h} ${slim ? "rounded-xl" : "rounded-2xl"} overflow-hidden shadow-xl shadow-black/10 ring-1 ring-black/5`}>
        {isFade ? (
          <div className="relative h-full w-full" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            {items.map((a, i) => (
              <div key={`${a.id}-${i}`} className={`absolute inset-0 transition-all duration-700 ease-out ${i === idx ? "opacity-100 scale-100 z-10" : `opacity-0 z-0 ${transition === "zoom" ? "scale-110" : "scale-100"}`}`} aria-hidden={i !== idx}>
                <AdSlide a={a} slim={slim} active={i === idx} onMediaDone={mediaDone} onMediaShown={(m) => onMediaShown(a, m)} onCta={() => trackEv(a, "click")} />
              </div>
            ))}
          </div>
        ) : (
          <div className="h-full overflow-hidden" ref={emblaRef}>
            <div className="flex h-full touch-pan-y">
              {items.map((a, i) => (
                <div key={`${a.id}-${i}`} className="relative min-w-0 flex-[0_0_100%] h-full px-0">
                  <div className={`h-full transition-transform duration-700 ${i === idx ? "scale-100" : "scale-[0.98]"}`}><AdSlide a={a} slim={slim} active={i === idx} onMediaDone={mediaDone} onMediaShown={(m) => onMediaShown(a, m)} onCta={() => trackEv(a, "click")} /></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {items.length > 1 ? (
          <>
            <button type="button" aria-label="Previous" data-testid="ad-prev" onClick={prev} className={`absolute left-2 top-1/2 -translate-y-1/2 z-20 place-items-center rounded-full bg-white/80 backdrop-blur text-zinc-900 shadow hover:bg-white transition-colors ${slim ? "hidden sm:grid h-6 w-6" : "grid h-8 w-8 sm:h-9 sm:w-9"}`}><ChevronLeft size={slim ? 14 : 18} /></button>
            <button type="button" aria-label="Next" data-testid="ad-next" onClick={next} className={`absolute right-2 top-1/2 -translate-y-1/2 z-20 place-items-center rounded-full bg-white/80 backdrop-blur text-zinc-900 shadow hover:bg-white transition-colors ${slim ? "hidden sm:grid h-6 w-6" : "grid h-8 w-8 sm:h-9 sm:w-9"}`}><ChevronRight size={slim ? 14 : 18} /></button>
          </>
        ) : null}
        {!slim ? (
          <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5">
            <span className="text-[9px] tracking-[0.18em] uppercase font-semibold text-white/80 bg-black/30 backdrop-blur px-2 py-0.5 rounded-full">Sponsored</span>
            {items.length > 1 ? <button type="button" aria-label={paused ? "Play" : "Pause"} data-testid="ad-playpause" onClick={() => setPaused(p => !p)} className="h-6 w-6 grid place-items-center rounded-full bg-black/30 backdrop-blur text-white hover:bg-black/50">{paused ? <Play size={11} /> : <Pause size={11} />}</button> : null}
          </div>
        ) : null}
      </div>
      {items.length > 1 ? (
        <div className={`flex items-center justify-center gap-1.5 ${slim ? "mt-1.5" : "mt-3"}`} data-testid="ad-dots">
          {items.map((a, i) => (
            <button key={`${a.id}-d${i}`} type="button" aria-label={`Go to slide ${i + 1}`} onClick={() => goTo(i)} style={{ height: 6, width: i === idx ? 24 : 6 }} className={`shrink-0 rounded-full transition-all duration-300 ${i === idx ? "bg-zinc-900" : "bg-zinc-400/70 hover:bg-zinc-600"}`} />
          ))}
        </div>
      ) : null}
      {cur ? <span className="sr-only">Slide {idx + 1} of {items.length}: {cur.title || cur.name}</span> : null}
    </section>
  );
}
